import { Dropdown } from '@/components/Dropdown/Dropdown';
import { DeleteMenuItem } from '@/components/Dropdown/MenuItems/DeleteMenuItem';
import { DownloadGroupMenuItem, VideoItem } from '@/components/Dropdown/MenuItems/DownloadGroupMenuItem';
import { EditMenuItem } from '@/components/Dropdown/MenuItems/EditMenuItem';
import { RawDataMenuItem, shouldShowRawMenuItem } from '@/components/Dropdown/MenuItems/RawDataMenuItem';
import { RenameMenuItem } from '@/components/Dropdown/MenuItems/RenameMenuItem';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import { EditableTitle } from '@/components/Title/EditableTitle';
import { useAuth } from '@/services/auth';
import { useGetTranslations, useUpdateTranslationGroup } from '@/state/translations';
import { AppRoutes } from '@/utils/types';
import { ITranslationGroup, LibraryItem, LibraryItemStatus } from '@d-id/types';
import styled from '@emotion/styled';
import { Divider, MenuList, MenuItem as MuiMenuItem } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { translationGroupToDelete } from '../../state';

const StyledList = styled(MenuList)`
    padding: 0;

    &:focus {
        outline: none;
    }
`;

const StyledDivider = styled(Divider)`
    margin: 1rem 0;
`;

const TitleContainer = styled(MuiMenuItem)`
    border-radius: 8px;
    padding: 6px;

    &:hover,
    &:focus {
        background-color: #fff;
    }
`;

const VideoTitle = styled(EditableTitle)`
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.05rem;
    overflow: hidden;

    &:focus,
    &:hover {
        border: none;
    }

    &:not(:focus) {
        text-overflow: ellipsis;
    }
`;

const CreationDate = styled('p')`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.black[600]};
    margin-top: 4px;
    margin-left: 8px;
`;

const OptionsButtonWrapper = styled.div`
    position: absolute;
    right: 16px;
    top: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 2;
`;

const dateFormatOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

interface Props {
    libraryItem: LibraryItem;
    onOpen?: () => void;
    onClose?: () => void;
}

export const TranslationOptionsList = ({ libraryItem, onOpen, onClose }: Props) => {
    const titleRef = useRef<HTMLDivElement | null>(null);
    const [videoName, setVideoName] = useState('');
    const [closed, setClosed] = useState<boolean | undefined>();
    const setTranslationGroupToDelete = useSetRecoilState(translationGroupToDelete);
    const { isAnonymous, user } = useAuth();
    const { refetch } = useGetTranslations(isAnonymous, libraryItem.id, false);
    const { mutateAsync: updateGroup } = useUpdateTranslationGroup();
    const navigate = useNavigate();

    const refetchTranslationsGroup = useCallback<() => Promise<{ data: VideoItem[] }>>(async () => {
        const res = await refetch();
        const data = (res?.data?.translations ?? []).map(video => ({
            id: video.id,
            name: video.name,
            language: video.language,
            videoUrl: video?.result_url,
            subtitlesUrl: video?.subtitles_url,
        }));

        return { data };
    }, [refetch]);

    useEffect(() => {
        if (libraryItem.name) {
            setVideoName(libraryItem.name);
        }
    }, [libraryItem.name]);

    const handleCloseMenu = () => {
        setClosed(true);
        onClose?.();
    };

    const handleOpenMenu = () => {
        setClosed(false);
        onOpen?.();
    };

    const renameTranslation = useCallback(
        async (name: string) => {
            await updateGroup({ id: libraryItem.id, name });
        },
        [updateGroup, libraryItem.id]
    );

    const handleEdit = ({ id }: LibraryItem) => {
        navigate(AppRoutes.TranslationDraft + `/${id}`);
    };

    const items = [
        {
            label: <DownloadGroupMenuItem group={libraryItem} closeMenu={handleCloseMenu} refetch={refetchTranslationsGroup} />,
        },
        { label: <EditMenuItem video={libraryItem} onEdit={handleEdit} closeMenu={handleCloseMenu} /> },
        { label: <RenameMenuItem video={libraryItem} titleRef={titleRef} /> },

        { label: libraryItem.status === LibraryItemStatus.Done ? <StyledDivider /> : null },
        {
            label: (
                <DeleteMenuItem
                    video={libraryItem}
                    closeMenu={handleCloseMenu}
                    onDelete={item => {
                        setTranslationGroupToDelete(item as unknown as ITranslationGroup);
                    }}
                />
            ),
        },
    ];

    if (shouldShowRawMenuItem(user)) {
        items.push({
            label: <RawDataMenuItem dataToShow={libraryItem} closeMenu={handleCloseMenu} />,
        });
    }

    return (
        <Dropdown
            closed={closed}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            trigger={
                <OptionsButtonWrapper>
                    <ThreeDotsCircle data-testid={`translation-options-three-dots-circle-${libraryItem.id}`} isActive={!closed} />
                </OptionsButtonWrapper>
            }
            items={items}
            renderMenuWrapper={children => (
                <StyledList>
                    <TitleContainer
                        onKeyDown={e => {
                            e.stopPropagation();
                        }}>
                        <VideoTitle
                            ref={titleRef}
                            fontSize={20}
                            value={videoName}
                            onChange={value => {
                                setVideoName(value);
                            }}
                            onBlur={async value => {
                                renameTranslation(value);
                            }}
                        />
                    </TitleContainer>
                    <CreationDate>Created {new Date(libraryItem.created_at).toLocaleDateString('en-US', dateFormatOptions)}</CreationDate>
                    <StyledDivider />
                    {children}
                </StyledList>
            )}
        />
    );
};
